import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import saveUser from "@insite/client-framework/Store/Pages/UserSetup/Handlers/SaveUser";
import setInitialValues from "@insite/client-framework/Store/Pages/UserSetup/Handlers/SetInitialValues";
import { getCurrentEditingUser, hasChanges } from "@insite/client-framework/Store/Pages/UserSetup/UserSetupSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ConfirmEmailChangeModal from "@insite/content-library/Components/ConfirmEmailChangeModal";
import AssignShipToAddressModal from "@insite/content-library/Widgets/UserSetup/AssignShipToAddressModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    editingUser: getCurrentEditingUser(state),
    currentUser: getCurrentAccountState(state).value,
    initialUserEmail: state.pages.userSetup.initialUserEmail,
    userAdministrationPageLink: getPageLinkByPageType(state, "UserAdministrationPage"),
    disableSaveButton:
        !hasChanges(state) ||
        !!state.pages.userSetup.emailErrorMessage ||
        !!state.pages.userSetup.firstNameErrorMessage ||
        !!state.pages.userSetup.lastNameErrorMessage,
    hasChanges: hasChanges(state),
});

const mapDispatchToProps = { setInitialValues, saveUser };

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

export interface UserSetupHeaderStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    emptyGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    actionButtonsHidden?: HiddenProps;
    assignShipToAddressButton?: ButtonPresentationProps;
    saveButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    overflowMenu?: OverflowMenuPresentationProps;
    overflowMenuClickables?: ClickablePresentationProps;
    overflowMenuSaveClickableHidden?: HiddenProps;
    overflowMenuCancelClickableHidden?: HiddenProps;
}

export const userSetupHeaderStyles: UserSetupHeaderStyles = {
    container: {
        gap: 8,
        css: css`
            padding-bottom: 20px;
        `,
    },
    titleGridItem: {
        width: [10, 10, 8, 7, 7],
    },
    actionsGridItem: {
        width: [2, 2, 4, 5, 5],
        css: css`
            justify-content: flex-end;
        `,
    },
    assignShipToAddressButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
    cancelButton: {
        color: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
};

const styles = userSetupHeaderStyles;

const UserSetupHeader = ({
    editingUser,
    currentUser,
    initialUserEmail,
    userAdministrationPageLink,
    disableSaveButton,
    hasChanges,
    setInitialValues,
    saveUser,
    history,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [isAssignShipToAddressModalOpen, setIsAssignShipToAddressModalOpen] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordError, setPasswordError] = useState("");

    if (!editingUser) {
        return null;
    }

    const isSelf = editingUser.id === currentUser?.id;

    const doSave = (currentPassword?: string) => {
        saveUser({
            currentPassword,
            onSuccess: () => {
                if (currentPassword !== undefined) {
                    setShowPasswordModal(false);
                    setPasswordError("");
                }
                toasterContext.addToast({ body: translate("Changes saved."), messageType: "success" });
                userAdministrationPageLink && history.push(userAdministrationPageLink.url);
            },
            onError: (message: string) => {
                if (currentPassword !== undefined) {
                    setPasswordError(message);
                } else {
                    toasterContext.addToast({ body: message, messageType: "danger" });
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    const saveClickHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        if (isSelf && editingUser.email !== initialUserEmail) {
            setPasswordError("");
            setShowPasswordModal(true);
            return;
        }
        doSave();
    };

    const assignShipToAddressClickHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        setIsAssignShipToAddressModalOpen(true);
    };

    const assignShipToAddressModalCloseHandler = () => setIsAssignShipToAddressModalOpen(false);

    const handlePasswordModalClose = () => {
        setShowPasswordModal(false);
        setPasswordError("");
    };

    return (
        <>
            <GridContainer {...styles.container}>
                <GridItem {...styles.titleGridItem}>
                    {createWidgetElement("Basic/PageTitle", {
                        fields: { overrideTitle: `${translate("Edit User")} - ${editingUser.userName}` },
                    })}
                </GridItem>
                <GridItem {...styles.actionsGridItem}>
                    <Hidden {...styles.actionButtonsHidden} below="lg">
                        <Button
                            {...styles.assignShipToAddressButton}
                            type="button"
                            onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) =>
                                assignShipToAddressClickHandler(event)
                            }
                            data-test-selector="userSetup_assignShipToAddress"
                        >
                            {translate("Assign Ship To Address")}
                        </Button>
                    </Hidden>
                    <Hidden {...styles.actionButtonsHidden} below="md">
                        {hasChanges && (
                            <Button
                                {...styles.cancelButton}
                                type="button"
                                onClick={() => setInitialValues({ userId: editingUser.id })}
                                data-test-selector="userSetupHeader_cancelButton"
                            >
                                {translate("Cancel")}
                            </Button>
                        )}
                    </Hidden>
                    <Hidden {...styles.actionButtonsHidden} below="md">
                        <Button
                            {...styles.saveButton}
                            type="button"
                            disabled={disableSaveButton}
                            onClick={saveClickHandler}
                            data-test-selector="userSetupHeader_saveButton"
                        >
                            {translate("Save")}
                        </Button>
                    </Hidden>
                    <Hidden {...styles.actionButtonsHidden} above="md">
                        <OverflowMenu position="end" {...styles.overflowMenu}>
                            <Clickable
                                {...styles.overflowMenuClickables}
                                onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) =>
                                    assignShipToAddressClickHandler(event)
                                }
                            >
                                {translate("Assign Ship To Address")}
                            </Clickable>
                            <Hidden {...styles.overflowMenuCancelClickableHidden} above="sm">
                                <Clickable
                                    {...styles.overflowMenuClickables}
                                    onClick={() => setInitialValues({ userId: editingUser.id })}
                                    disabled={!hasChanges}
                                >
                                    {translate("Cancel")}
                                </Clickable>
                            </Hidden>
                            <Hidden {...styles.overflowMenuSaveClickableHidden} above="sm">
                                <Clickable
                                    {...styles.overflowMenuClickables}
                                    disabled={disableSaveButton}
                                    onClick={saveClickHandler}
                                >
                                    {translate("Save")}
                                </Clickable>
                            </Hidden>
                        </OverflowMenu>
                    </Hidden>
                </GridItem>
            </GridContainer>
            <AssignShipToAddressModal
                isModalOpen={isAssignShipToAddressModalOpen}
                closeModalHandler={assignShipToAddressModalCloseHandler}
            />
            <ConfirmEmailChangeModal
                isOpen={showPasswordModal}
                onClose={handlePasswordModalClose}
                onConfirm={doSave}
                passwordError={passwordError}
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(UserSetupHeader)),
    definition: {
        group: "User Setup",
        displayName: "Header",
        allowedContexts: ["UserSetupPage"],
    },
};

export default widgetModule;
